"use client";

import React, { useState, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STC = "STC Rating";
const FIRE = "Fire Rating";
const SITES = "Mfg Site Restrictions";

const numericSort = (a, b) => {
  const na = parseFloat(a);
  const nb = parseFloat(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return String(a).localeCompare(String(b));
  return na - nb;
};

/**
 * Appendix M door finder. `columns` (from an uploaded spreadsheet) sets the
 * column order; without it the keys of the first row are used.
 */
const DynamicTable = ({
  data,
  columns: columnsProp,
}: {
  data: Record<string, any>[];
  columns?: string[];
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [stcFilter, setStcFilter] = useState("all");
  const [fireFilter, setFireFilter] = useState("all");
  const [siteRestrictions, setSiteRestrictions] = useState("all");
  const itemsPerPage = 10;
  const columns = columnsProp?.length ? columnsProp : Object.keys(data[0] ?? {});
  const hasColumn = (name) => columns.includes(name);

  // Options come from the data, so a new or renamed site needs no code change.
  const siteOptions = useMemo(
    () =>
      [
        ...new Set(
          data.flatMap((item) =>
            String(item[SITES] ?? "")
              .split(",")
              .map((site) => site.trim())
              .filter(Boolean),
          ),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [data],
  );

  // Any change to the search or filters starts again from page 1.
  const resetPage = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };
  const printTableRef = useRef(null);

  // Helper to trigger print
  const handlePrint = () => {
    // Show the print table and trigger print
    window.print();
  };

  const filteredAndSortedData = useMemo(() => {
    return data
      .filter(
        (item) =>
          Object.values(item).some((value) =>
            value
              ?.toString()
              ?.toLowerCase()
              ?.includes(searchTerm?.toLowerCase())
          ) &&
          (stcFilter === "all" || item[STC] === stcFilter) &&
          (fireFilter === "all" || item[FIRE] === fireFilter) &&
          (siteRestrictions === "all" ||
            String(item[SITES] ?? "")
              .split(",")
              .map((site) => site.trim())
              .includes(siteRestrictions))
      )
      .sort((a, b) => {
        if (sortColumn === "") return 0;
        const order = numericSort(a[sortColumn] ?? "", b[sortColumn] ?? "");
        return sortDirection === "asc" ? order : -order;
      });
  }, [
    data,
    searchTerm,
    sortColumn,
    sortDirection,
    stcFilter,
    fireFilter,
    siteRestrictions,
  ]);

  const pageCount = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return (
    <div className="space-y-4">
      {/* Print Button */}
      <Button onClick={handlePrint} className="mb-2" type="button">
        Print Table
      </Button>

      {/* Print-Only Table (hidden on screen, visible when printing) */}
      <div
        ref={printTableRef}
        className="print-table-container"
        style={{ display: "none" }}
        aria-hidden="true"
      >
        <table
          className="print-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "12px",
          }}
        >
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  style={{
                    border: "1px solid #ccc",
                    padding: "4px",
                    background: "#f9f9f9",
                    fontWeight: "bold",
                  }}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item, idx) => (
              <tr key={idx}>
                {columns.map((column) => (
                  <td
                    key={column}
                    style={{
                      border: "1px solid #ccc",
                      padding: "4px",
                      wordBreak: "break-word",
                    }}
                  >
                    {item[column]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Print CSS (inline for simplicity, can be moved to a CSS file) */}
      <style>{`
        @media print {
          body * {
            display: hidden !important;
          }
          nav, header,  .hero{
            display: none !important;
          }
          .print-table-container, .print-table-container * {
            visibility: visible !important;
          }
          .print-table-container {
            display: block !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100vw;
            background: white;
            z-index: 9999;
            padding: 0;
            margin: 0;
          }
          .print-table {
            width: 100vw !important;
            font-size: 9px !important;
          }
          .print-table th, .print-table td {
            padding: 2px 4px !important;
            border: 1px solid #888 !important;
            word-break: break-word !important;
            max-width: 120px;
            font-size: 9px !important;
          }
          .print-table th {
            background: #eee !important;
            font-weight: bold !important;
          }
          /* Hide pagination, filters, and other UI */
          .space-y-4 > *:not(.print-table-container) {
            display: none !important;
          }
        }
      `}</style>

      <Input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => resetPage(setSearchTerm)(e.target.value)}
        className="w-full"
      />
      <div className="flex space-x-4">
        {hasColumn(STC) && (
        <Select value={stcFilter} onValueChange={resetPage(setStcFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter STC Rating" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All STC Ratings</SelectItem>
            {[...new Set(data.map((item) => item[STC]).filter(Boolean))]
              .sort(numericSort)
              .map((rating) => (
                <SelectItem
                  key={rating}
                  value={rating.toString()}
                  className="hover:bg-[#f1f1f1] py-2 border-b border-[#eee]"
                >
                  {rating}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        )}
        {hasColumn(FIRE) && (
        <Select value={fireFilter} onValueChange={resetPage(setFireFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter Fire Rating" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Fire Ratings</SelectItem>
            {[...new Set(data.map((item) => item[FIRE]).filter(Boolean))]
              .sort(numericSort)
              .map((rating) => (
                <SelectItem
                  key={rating}
                  value={rating.toString()}
                  className="hover:bg-[#f1f1f1] py-2 border-b border-[#eee]"
                >
                  {rating}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        )}
        {hasColumn(SITES) && (
        <Select value={siteRestrictions} onValueChange={resetPage(setSiteRestrictions)}>
          <SelectTrigger className="w-auto">
            <SelectValue placeholder="Filter Mfg Site Restrictions" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Mfg Site Restrictions</SelectItem>
            {siteOptions.map((rating) => (
              <SelectItem
                key={rating}
                value={rating.toString()}
                className="hover:bg-[#f1f1f1] py-2 border-b border-[#eee]"
              >
                {rating}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
      </div>
      <Table className="min-h-[1050px]">
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column}
                onClick={() => handleSort(column)}
                className="cursor-pointer"
              >
                {column}
                {sortColumn === column && (
                  <span className="ml-1">
                    {sortDirection === "asc" ? "▲" : "▼"}
                  </span>
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((item, index) => (
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={column}>{item[column]}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {pageCount}
        </div>
        <div className="space-x-2">
          <Button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, pageCount))
            }
            disabled={currentPage >= pageCount}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DynamicTable;
